import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  DatePicker,
  Select,
  Card,
  Divider,
  Tooltip,
  Alert,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchComparativeStatementRequest } from '../../redux/actions/quotationActions';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toFixed(2)}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');

const isExpired = (validUntil) =>
  validUntil && dayjs(validUntil).isBefore(dayjs(), 'day');

/**
 * ComparativeStatement
 *
 * Standalone component for the comparative tab inside QuotationList.
 */
const ComparativeStatement = () => {
  const dispatch = useDispatch();
  const { vendors } = useSelector((s) => s.vendors || {});
  const { products } = useSelector((s) => s.products || {});
  const { comparativeData, loading } = useSelector((s) => s.quotations);

  const [dateRange, setDateRange]       = useState(null);
  const [vendorFilter, setVendorFilter] = useState([]);
  const [productFilter, setProductFilter] = useState([]);

  const load = useCallback(() => {
    const params = {};
    if (dateRange?.[0]) params.date_from  = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.date_to    = dateRange[1].format('YYYY-MM-DD');
    if (vendorFilter.length)  params.vendor_ids  = vendorFilter.join(',');
    if (productFilter.length) params.product_ids = productFilter.join(',');
    dispatch(fetchComparativeStatementRequest(params));
  }, [dispatch, dateRange, vendorFilter, productFilter]);

  const quoteColumns = [
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          {isExpired(record.valid_until) && (
            <Tag color="red" style={{ fontSize: 10 }}>Expired</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Quotation #',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      width: 150,
      render: (v) => <Text style={{ color: '#1890ff', fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Quoted Rate',
      dataIndex: 'quoted_rate',
      key: 'quoted_rate',
      width: 110,
      align: 'right',
      render: (v) => fmtMoney(v),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 80,
      align: 'center',
      render: (v) => v > 0 ? <Tag color="green">{v}%</Tag> : '—',
    },
    {
      title: 'GST',
      dataIndex: 'tax_slab',
      key: 'tax_slab',
      width: 70,
      align: 'center',
      render: (v) => v ? <Tag color="orange">{v}%</Tag> : '0%',
    },
    {
      title: 'Effective Rate',
      dataIndex: 'effective_rate',
      key: 'effective_rate',
      width: 130,
      align: 'right',
      render: (v, record) => {
        const isLowest = record._isLowest;
        return (
          <Space size={4}>
            <Text
              strong
              style={{
                fontSize: 13,
                color: isLowest ? '#52c41a' : '#1890ff',
              }}
            >
              {fmtMoney(v)}
            </Text>
            {isLowest && (
              <Tooltip title="Lowest effective rate">
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      width: 110,
      render: (v) => {
        if (!v) return <Text type="secondary">—</Text>;
        return (
          <span style={{ color: isExpired(v) ? '#ff4d4f' : 'inherit' }}>
            {fmtDate(v)}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => {
        const map = {
          received:  { color: 'blue',    text: 'Active'    },
          expired:   { color: 'orange',  text: 'Expired'   },
          converted: { color: 'success', text: 'Converted' },
        };
        const cfg = map[v] || { color: 'default', text: v };
        return <Tag color={cfg.color} style={{ fontSize: 11 }}>{cfg.text}</Tag>;
      },
    },
  ];

  return (
    <div>
      {/* ── Filters ──────────────────────────────────────────────────── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            style={{ width: 260 }}
            format="DD MMM YYYY"
            value={dateRange}
            onChange={setDateRange}
            allowClear
            placeholder={['From Date', 'To Date']}
          />
          <Select
            mode="multiple"
            placeholder="Filter vendors"
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount={2}
            value={vendorFilter}
            onChange={setVendorFilter}
          >
            {(vendors || []).map((v) => (
              <Select.Option key={v.id} value={v.id}>{v.firm_name}</Select.Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            placeholder="Filter products"
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount={2}
            value={productFilter}
            onChange={setProductFilter}
          >
            {(products || []).map((p) => (
              <Select.Option key={p.id} value={p.id}>
                {p.product_name || p.name}
              </Select.Option>
            ))}
          </Select>
          <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Generate
          </Button>
          {(dateRange || vendorFilter.length || productFilter.length) && (
            <Button
              icon={<ClockCircleOutlined />}
              onClick={() => { setDateRange(null); setVendorFilter([]); setProductFilter([]); }}
            >
              Clear
            </Button>
          )}
        </Space>
      </Card>

      {/* ── Per-product sections ──────────────────────────────────────── */}
      {!loading && comparativeData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No quotations found. Create quotations from multiple vendors, then click{' '}
              <strong>Generate</strong> to compare rates.
            </span>
          }
        />
      ) : (
        comparativeData.map((productItem) => {
          const sortedQuotes = [...(productItem.quotes || [])].sort(
            (a, b) => a.effective_rate - b.effective_rate
          );
          const lowestRate = productItem.lowest_rate;
          const quotesWithFlag = sortedQuotes.map((q) => ({
            ...q,
            _isLowest: Math.abs(q.effective_rate - lowestRate) < 0.001,
          }));

          return (
            <div key={productItem.product_id} style={{ marginBottom: 24 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: '#f0f5ff',
                  borderRadius: '6px 6px 0 0',
                  border: '1px solid #adc6ff',
                  borderBottom: 'none',
                }}
              >
                <Space>
                  <Text strong style={{ fontSize: 15 }}>{productItem.product_name}</Text>
                  {productItem.unit && (
                    <Tag color="geekblue" style={{ fontSize: 11 }}>{productItem.unit}</Tag>
                  )}
                  <Tag color="blue">{quotesWithFlag.length} quote{quotesWithFlag.length !== 1 ? 's' : ''}</Tag>
                </Space>
                <Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>Lowest Rate Vendor:</Text>
                  <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontWeight: 600 }}>
                    {productItem.lowest_vendor_name} — {fmtMoney(lowestRate)}
                  </Tag>
                </Space>
              </div>

              <Table
                columns={quoteColumns}
                dataSource={quotesWithFlag}
                rowKey="quotation_id"
                pagination={false}
                size="small"
                loading={loading}
                rowClassName={(record) =>
                  record._isLowest ? 'comparative-row-winner' : ''
                }
                style={{
                  border: '1px solid #adc6ff',
                  borderTop: 'none',
                  borderRadius: '0 0 6px 6px',
                  overflow: 'hidden',
                }}
              />
            </div>
          );
        })
      )}

      {/* ── Legend ────────────────────────────────────────────────────── */}
      {comparativeData.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            background: '#fafafa',
            borderRadius: 6,
            border: '1px solid #f0f0f0',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
            Green row = lowest effective rate (after discount, before tax).
            Effective Rate = Quoted Rate × (1 − Discount%).
          </Text>
        </div>
      )}
    </div>
  );
};

export default ComparativeStatement;
